import React, { useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { Alert, Button, Snackbar } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import { GENERAL_FONT } from '../../constants/theme';
import { TreatmentDto } from '../../lib/services/treatmentsService';
import { TreatmentPlanStatus } from '../../lib/services/treatmentPlanService';
import { useMonthlySummaryText } from './MonthlySummaryCopyButton';
import { reportError } from '../../lib/sentry';

type TreatmentPlan = {
  id: string;
  treatmentId: string;
  startDate: string;
  endDate?: string | null;
  status: TreatmentPlanStatus;
};

type EffectWindow = {
  startDate: string;
  endDate: string;
  avgHeadacheDaysPerMonth: number;
  avgMigraineDaysPerMonth: number;
};

type TreatmentEffectResponse = {
  planId: string;
  plan: {
    status: 'active' | 'stopped';
  };
  windows: { before: EffectWindow; after: EffectWindow };
  derived: { migraineReductionPct: number };
};

type Props = {
  disabled?: boolean;
  effect: TreatmentEffectResponse;
  selectedPlan: TreatmentPlan;
  plans: TreatmentPlan[];
  treatmentCatalog: TreatmentDto[];
  selectedTreatmentName: string;
  beforeRangeLabel: string; // "YYYY-MM-DD – YYYY-MM-DD"
  afterRangeLabel: string; // "YYYY-MM-DD – YYYY-MM-DD"
};

function num1(n: number) {
  if (!Number.isFinite(n)) return '0.0';
  return n.toFixed(1);
}

function pct(n: number) {
  if (!Number.isFinite(n)) return '0%';
  return `${Math.round(n)}%`;
}

function parseRangeLabel(range: string): { start: string; end: string } | null {
  const parts = String(range || '')
    .split(' – ')
    .map((s) => s.trim());
  if (parts.length !== 2) return null;
  const [start, end] = parts;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(end)) return null;
  return { start, end };
}

function overlapsYmd(
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string
) {
  const as = dayjs(aStart, 'YYYY-MM-DD', true);
  const ae = dayjs(aEnd, 'YYYY-MM-DD', true);
  const bs = dayjs(bStart, 'YYYY-MM-DD', true);
  const be = dayjs(bEnd, 'YYYY-MM-DD', true);
  if (!as.isValid() || !ae.isValid() || !bs.isValid() || !be.isValid())
    return false;
  return as.valueOf() <= be.valueOf() && ae.valueOf() >= bs.valueOf();
}

function findTreatmentName(catalog: TreatmentDto[], treatmentId: string) {
  return (
    (catalog || []).find((t) => t.treatmentId === treatmentId)?.displayName ??
    treatmentId
  );
}

export function buildCombinedCopyText(args: {
  t: TFunction;
  effect: TreatmentEffectResponse;
  selectedPlan: TreatmentPlan;
  plans: TreatmentPlan[];
  treatmentCatalog: TreatmentDto[];
  selectedTreatmentName: string;
  beforeRangeLabel: string;
  afterRangeLabel: string;
  monthlyText: string;
}) {
  const { t } = args;

  const { before } = args.effect.windows;
  const { after } = args.effect.windows;

  const beforeRange = parseRangeLabel(args.beforeRangeLabel);
  const afterRange = parseRangeLabel(args.afterRangeLabel);

  const evalStart = beforeRange?.start ?? before.startDate;
  const evalEnd = afterRange?.end ?? after.endDate;

  const selectedPlanRange = args.selectedPlan.endDate
    ? `${args.selectedPlan.startDate} – ${args.selectedPlan.endDate}`
    : `${args.selectedPlan.startDate} – ${t('treatmentEffect.ongoing')}`;

  const otherOverlapping = args.plans
    .filter((p) => p.id !== args.selectedPlan.id)
    .filter((p) => {
      const pEndForOverlap = p.endDate ?? evalEnd;
      return overlapsYmd(p.startDate, pEndForOverlap, evalStart, evalEnd);
    })
    .map((p) => {
      const name = findTreatmentName(args.treatmentCatalog, p.treatmentId);
      const range = p.endDate
        ? `${p.startDate} – ${p.endDate}`
        : `${p.startDate} – ${t('treatmentEffect.ongoing')}`;
      return { name, range };
    });

  const lines: string[] = [];

  lines.push(t('treatmentEffect.effectSectionHeader'));
  lines.push(
    t('treatmentEffect.selectedPlanLine', {
      name: args.selectedTreatmentName,
      range: selectedPlanRange,
    })
  );
  lines.push('');
  lines.push(t('treatmentEffect.beforeLine', { range: args.beforeRangeLabel }));
  lines.push(t('treatmentEffect.afterLine', { range: args.afterRangeLabel }));
  lines.push('');
  lines.push(t('treatmentEffect.resultsHeader'));
  lines.push(
    t('treatmentEffect.beforeTreatmentSentence', {
      headache: num1(before.avgHeadacheDaysPerMonth),
      migraine: num1(before.avgMigraineDaysPerMonth),
    })
  );
  lines.push(
    t('treatmentEffect.afterTreatmentSentence', {
      headache: num1(after.avgHeadacheDaysPerMonth),
      migraine: num1(after.avgMigraineDaysPerMonth),
    })
  );
  lines.push(
    t('treatmentEffect.migraineChangeSentence', {
      pct: pct(args.effect.derived.migraineReductionPct),
    })
  );

  if (otherOverlapping.length > 0) {
    lines.push('');

    lines.push(t('treatmentEffect.otherActivePlansHeader'));
    for (const p of otherOverlapping) {
      lines.push(
        t('treatmentEffect.otherActivePlanItem', {
          name: p.name,
          range: p.range,
        })
      );
    }
  }

  const monthBlock = String(args.monthlyText ?? '').trim();
  if (monthBlock) {
    lines.push('');
    lines.push(t('treatmentEffect.monthlySectionHeader'));
    lines.push('');
    lines.push(monthBlock);
  }

  return lines.join('\n').trim();
}

const CombinedCopyToEmrButton: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const monthlyText = useMonthlySummaryText();

  const combinedText = useMemo(
    () =>
      buildCombinedCopyText({
        t,
        effect: props.effect,
        selectedPlan: props.selectedPlan,
        plans: props.plans,
        treatmentCatalog: props.treatmentCatalog,
        selectedTreatmentName: props.selectedTreatmentName,
        beforeRangeLabel: props.beforeRangeLabel,
        afterRangeLabel: props.afterRangeLabel,
        monthlyText,
      }),
    [
      t,
      props.effect,
      props.selectedPlan,
      props.plans,
      props.treatmentCatalog,
      props.selectedTreatmentName,
      props.beforeRangeLabel,
      props.afterRangeLabel,
      monthlyText,
    ]
  );

  const isDisabled = Boolean(props.disabled) || !combinedText.trim();

  return (
    <>
      <Button
        variant="contained"
        size="small"
        sx={{ textTransform: 'none', fontFamily: GENERAL_FONT }}
        disabled={isDisabled}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(combinedText);
          } catch (err) {
            reportError(err, { level: 'warning', tags: { area: 'clipboard' } });
            return;
          }

          flushSync(() => setCopied(false));
          requestAnimationFrame(() => setCopied(true));
        }}
      >
        {t('treatmentEffect.copySummaryToEmr')}
      </Button>

      <Snackbar
        open={copied}
        autoHideDuration={1500}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setCopied(false);
        }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setCopied(false)}
        >
          {t('copiedSuccessfully')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CombinedCopyToEmrButton;
